import Vue from 'vue'
import Router from 'vue-router'
import Login from '@/components/Login'
import Signup from '@/components/Signup'
import DashboardLayout from "../components/layuot/DashboardLayout"
import Dashboard from "@/components/Dashboard"
import Subscriptions from "@/components/Subscriptions"
import NotFound from "@/components/NotFound"
import Profile from "@/components/Profile"
import ResetPassword from "../components/ResetPassword";
import EmailConfirmed from "../components/EmailConfirmed";
import ResetPasswordForm from "@/components/ResetPasswordForm";

import constants from "@/config/constants";
import { readPendingExampleFromQuery, takePendingExample } from '@/utils/pending_example';
import LoginSocialRedirect from "@/components/LoginSocialRedirect";
import Transactions from "@/components/Transactions";
import ResidentialProxies from "@/components/ResidentialProxies.vue";
import WebScrapingAPI from "@/components/WebScrapingAPI.vue";
import DatacenterProxies from "@/components/DatacenterProxies.vue";
import IPWhitelist from "@/components/IPWhitelist.vue";
import OAuthConsent from "@/components/OAuthConsent.vue";
import {rememberOAuthRequest} from "@/utils/oauth_consent";
import MCP from "@/components/MCP.vue";
Vue.use(Router)

Vue.router = new Router({
    mode: "history",
    routes: [{
        path: '/login',
        name: "Login",
        component: Login,
        meta: {
            auth: false,
            title: "Login"
        }
    },
    {
        path: '/login/:type',
        name: "LoginSocialRedirect",
        component: LoginSocialRedirect,
        meta: {
            auth: false,
            title: "Login"
        }
    },
    {
        path: '/signup',
        name: "Signup",
        component: Signup,
        meta: {
            auth: false,
            title: "Sign Up"
        }
    },
    {
        path: '/reset_password',
        name: "ResetPassword",
        component: ResetPassword,
        meta: {
            auth: false,
            title: "Reset Password"
        }
    },
    {
        path: '/reset_password/:token',
        name: "ResetPasswordForm",
        component: ResetPasswordForm,
        meta: {
            auth: false,
            title: "Reset Password"
        }
    },
    {
        path: '/email_confirmed/:token',
        name: "EmailConfirmed",
        component: EmailConfirmed,
        meta: {
            auth: false,
            title: "Email Confirmation"
        }
    },
    {
        path: '/oauth/authorize',
        name: "OAuthConsent",
        component: OAuthConsent,
        meta: {
            auth: true,
            title: "Authorize application"
        }
    },
    {
        path: '/',
        name: "DashboardLayout",
        component: DashboardLayout,
        redirect: "/dashboard",
        children: [{
            path: "dashboard",
            name: "Dashboard",
            component: Dashboard,
            meta: {
                auth: true,
                title: "Dashboard"
            }
        },
        {
            path: "webscrapingapi",
            name: "WebScrapingAPI",
            component: WebScrapingAPI,
            meta: {
                auth: true,
                title: "Web Scraping API"
            }
        },
        {
            path: "mcp",
            name: "MCP",
            component: MCP,
            meta: {
                auth: true,
                title: "Web MCP"
            }
        },
        {
            path: "residential",
            name: "ResidentialProxies",
            component: ResidentialProxies,
            meta: {
                auth: true,
                title: "Residential Proxies"
            }
        },
        {
            path: "datacenter",
            name: "DatacenterProxies",
            component: DatacenterProxies,
            meta: {
                auth: true,
                title: "Datacenter Proxies"
            }
        },
        {
            path: "subscriptions",
            name: "Subscriptions",
            component: Subscriptions,
            meta: {
                auth: true,
                title: "Subscriptions"
            }
        },
        {
            path: '/transactions',
            name: "Transactions",
            component: Transactions,
            meta: {
                auth: true,
                title: "Transactions"
            }
        },
        {
            path: '/profile',
            name: "Profile",
            component: Profile,
            meta: {
                auth: true,
                title: "Profile"
            }
        },
        {
            path: '/whitelist',
            name: "IPWhitelist",
            component: IPWhitelist,
            meta: {
                auth: true,
                title: "IP Whitelist"
            }
        },
        ]
    },
    {
        path: '*',
        name: "NotFound",
        component: NotFound,
        meta: {
            auth: false,
            title: "Not Found"
        }
    }
    ],
    scrollBehavior: (to, from, savedPosition) =>{
        if (savedPosition) {
            return savedPosition;
        }
        if (to.hash) {
            return { el: to.hash, behavior: "smooth" };
        } else {
            window.scrollTo(0, 0);
        }
    },
})

// Register capture before vue-auth's guard: signed-in users never mount Signup/Login.
Vue.router.beforeEach((to, from, next) => {
    if (to.name === 'Signup' || to.name === 'Login') {
        readPendingExampleFromQuery(to);
    }
    next();
});

// beforeResolve runs after authentication guards, including a cold-start refresh.
// Keep the cookie while signed out; consume it only on an allowed protected route.
Vue.router.beforeResolve((to, from, next) => {
    if (!to.matched.some(record => record.meta.auth === true)) return next();
    // An OAuth authorization in progress takes priority; the example stays pending for a later visit.
    if (to.name === 'OAuthConsent') return next();
    const pending = takePendingExample();
    if (!pending) return next();
    const query = {
        ...(to.name === 'WebScrapingAPI' ? to.query : {}),
        response_type: pending.example,
        url: pending.url,
        browser: String(pending.browser),
    };
    if (to.name === 'WebScrapingAPI' &&
        ['response_type', 'url', 'browser'].every(key => to.query[key] === query[key])) {
        return next();
    }
    next({ name: 'WebScrapingAPI', query, replace: true });
});

// Registered before vue-auth's guard: a logged-out user is sent to /login before the consent
// page mounts, so the request id has to be saved here to survive the login round trip.
Vue.router.beforeEach((to, from, next) => {
    if (to.name === "OAuthConsent" && to.query.request_id) {
        rememberOAuthRequest(to.query.request_id);
    }
    next();
});

// eslint-disable-next-line no-unused-vars
Vue.router.afterEach((to) => {
    // Use next tick to handle router history correctly
    // see: https://github.com/vuejs/vue-router/issues/914#issuecomment-384477609
    Vue.nextTick(() => {
        document.title = to.meta.title ? `${to.meta.title} - ${constants.TEXT.DEFAULT_TITLE}` : constants.TEXT.DEFAULT_TITLE;
    });
});

export default Vue.router;
